import type { CategoryPrompt } from "../types";

import React from "react";

import {
  CircleCheck,
  Expand,
  Footprints,
  Palette,
  PersonStanding,
  Sparkles,
  Tags,
  Timer,
  UserCheck,
} from "lucide-react";

export const shoesPrompts: CategoryPrompt[] = [
  {
    icon: <Footprints />,
    title: "Fit Finder Quiz",
    description:
      "Build a fit quiz asking about width, arch type, and typical problems, then recommend a product and size with confidence.",
    prompt: `**Build a fit finder quiz that diagnoses foot characteristics and common problems to recommend the perfect shoe and size with confidence.**

**QUIZ FOCUS:** e.g., "Find Your Perfect Fit" OR "What Shoe Is Right for Your Feet?" OR "Custom Fit Recommendations"

**FIT FACTORS:** e.g., Width, arch type, foot shape, pain points, activities (15-30 shoe styles to match from)

**Quiz Taker Type:** Choose one:
- [ ] Fit-frustrated (shoes never fit right)
- [ ] Online buyers (hesitant without trying on)
- [ ] Problem-solvers (blisters, pain, discomfort)

**Tone & Style:**
- Expert shoe fitter consultant
- Empathetic to fit struggles
- Technical but accessible
- "Finally, shoes that fit" promise

**Quiz Question Guidelines:** Each question should:
- Address specific fit characteristics
- Use visual foot shape examples
- Build toward precise recommendations
- Include pain point identification
- 8-10 total questions maximum

**Email Gate Strategy:**
- Place after question #6 (after core fit data)
- Frame as "Get your personalized fit recommendation"
- Offer incentive: "Unlock fit + 20% off your perfect pair"
- Single field: email only
- Trust line: "Join 400,000+ with perfect-fitting shoes"

**Results Page Elements:**
- **Personalized Headline:** "Your perfect fit: [Style Name] in size [X] width [Y]"
- **Fit Profile Summary:** Width, arch, foot shape characteristics recap
- **Recommended Shoe:** Specific style matching their fit needs
- **Why This Fits You:** Design features addressing their foot characteristics
- **Size Recommendation:** Exact size + width with confidence level
- **Fit Features Highlighted:**
  - Width accommodation
  - Arch support level
  - Toe box space
  - Heel cup design
  - Pressure point relief
- **Sizing Guidance:** US size with width letter (e.g., 9.5D, 10EE)
- **How It Addresses Your Issues:** Connection between problems and shoe solutions
- **Product Details:** Materials, construction, comfort technology
- **Fit Guarantee:** "Perfect fit or free returns/exchanges"
- **Product Cards Include:** Image, fit callouts, size recommendation, "Get Your Fit"
- **Match Reasoning:** "You have [arch type] with [width], so this [feature] prevents..."
- **Alternative Options:** 2-3 other styles if preferences differ
- **Sock Recommendations:** Thickness that works with this fit
- **Break-In Expectations:** What to expect wearing first time
- **Fit Adjustment Tips:** If between sizes, guidance on which way to go
- **Customer Fit Reviews:** Filtered to similar foot characteristics
- **Try-On Guide:** What to check when shoes arrive
- **CTAs:** "Get Your Perfect Fit" and "Order with Confidence"
- **Retake Option:** "Foot needs changed? Retake quiz"

**Conversion Tactics Use:**
- Precise size + width recommendation
- Fit problem validation
- Perfect fit guarantee
- Customer reviews from similar feet
- Free returns emphasis
- Fit technology explanations

**Key Quiz Questions to Include:**
- **Current Shoe Size:** What they usually wear
- **Width Issues:** Shoes too narrow/wide, pinching, gapping
- **Arch Type:** High, medium, low/flat (with visual test)
- **Foot Shape:** Narrow heel/wide forefoot, square toes, etc.
- **Common Problems:** Blisters, bunions, plantar fasciitis, heel pain, toe crowding
- **Pressure Points:** Where shoes hurt (toes, sides, heel, arch)
- **Activity Use:** Walking, running, standing all day, casual
- **Sock Thickness:** Thin, medium, thick preference
- **Current Fit Frustrations:** What doesn't work with current shoes
- **Special Needs:** Orthotics, medical conditions, swelling

**Goal:** Convert fit-anxious online shoe shoppers into confident buyers by providing precise, personalized size and width recommendations based on their unique foot characteristics and pain points, backed by perfect fit guarantee and detailed explanations of why this specific shoe solves their fit problems - eliminating sizing fear and returns.`,
  },
  {
    icon: <PersonStanding />,
    title: "Fit Problem Listicle Advertorial",
    description:
      "Build a listicle addressing common fit problems, with each linking to my products with fit technology explained.",
    prompt: `**Build a fit problem listicle that identifies common shoe issues and positions your products as the engineered solutions with technology explanations.**

**TOPIC:** e.g., "7 Reasons Your Shoes Never Fit Right" OR "5 Fit Problems Ruining Your Feet" OR "Why Your Shoes Hurt (And How to Fix It)"

**PRODUCTS FEATURED:** e.g., "Our wide-width design solves problem #3" OR "Our arch support system fixes issue #5"

**Reader Type:** Choose one:
- [ ] Chronic fit sufferers (shoes always hurt)
- [ ] Problem-aware (know what's wrong, need solution)
- [ ] Shoe shoppers (researching before buying)

**Tone & Style:**
- Empathetic shoe fit expert
- Technical but accessible
- "It's not you, it's the shoes" validation
- Engineering-focused solutions

**CTA Guidelines:** Each CTA should:
- Reference problem solved ("No More Blisters")
- Use fit technology language ("Engineered for Wide Feet")
- Include comfort benefit ("All-Day Comfort")
- Emphasize design solution ("Built Different")

**Conversion Tactics Use:**
- Fit problem validation
- Biomechanics explanations
- Your design technology features
- Before/after customer experiences
- Fit guarantee emphasis
- Podiatrist endorsements

**Key Elements to Include:**
- **Hook:** Relatable shoe pain moment (2-3 paragraphs on universal frustration)
- **List Items (6-8 problems):** Numbered fit issue → biomechanics → your solution
- **Problem Structure Per Item:**
  - Problem headline: "#X: [Common fit problem everyone experiences]"
  - What it feels like: Detailed symptom description
  - Why this happens: Biomechanical or design explanation
  - Long-term damage: Foot health consequences if ignored
  - The solution: Your shoe with specific design fix
  - Fit technology: Engineering feature addressing problem
  - How it works: Biomechanics of the solution
  - Materials/construction: What makes it possible
  - Customer testimonial: Someone who solved this exact problem
  - Comparison visual: Standard shoe vs. your design
- **Product Callouts:** Fit technology highlights with "Shop Solution" CTA
- **Technology Explanations:** Deep dives on fit innovations (2-3 throughout)
- **Biomechanics Education:** Why feet need certain support/space
- **Podiatrist Validation:** Expert quotes on fit problems and solutions
- **Fit Feature Diagrams:** Cross-sections showing technology
- **Customer Fit Stories:** Before painful shoes vs. after yours
- **CTAs:** After problem #2, after problem #5, conclusion (5-7 total)
- **FAQ:** 4-5 questions (Works for my feet? Try before buy? Width options? Break-in period?)

**Goal:** Convert fit-frustrated shoppers into confident buyers by validating their chronic shoe problems, explaining the biomechanical causes, and demonstrating how your engineered fit technologies provide targeted solutions that standard shoes can't offer - backed by podiatrist approval and customer success stories proving lasting comfort.`,
  },
  {
    icon: <UserCheck />,
    title: "Activity-Specific Landing Page",
    description:
      "Build a page targeting [a specific activity, like walking] with relevant features, performance specs, and testimonials from users in that activity.",
    prompt: `**Build an activity-specific landing page that targets a particular use case with relevant performance features and testimonials from similar activity users.**

**ACTIVITY FOCUS:** e.g., "Best Shoes for Walking" OR "Running Shoes for Marathon Training" OR "Standing All Day at Work" OR "Hiking & Trail Shoes"

**PERFORMANCE PRIORITIES:** e.g., Cushioning, stability, traction, durability, breathability

**Target Customer:** Choose one:
- [ ] Activity enthusiasts (serious about performance)
- [ ] Daily users (need for regular activity)
- [ ] Problem-solvers (current shoes failing for activity)

**Tone & Style:**
- Activity expert advisor
- Performance-focused
- User experience validation
- "Built for this" confidence

**CTA Guidelines:** Each CTA should:
- Reference activity ("Perfect for Walking")
- Use performance language ("Go Further")
- Include activity benefit ("Mile After Mile")
- Emphasize suitability ("Designed for This")

**Conversion Tactics Use:**
- Activity-specific feature callouts
- Performance testing data
- User testimonials from same activity
- Distance/duration capabilities
- Professional endorsements (trainers, coaches)
- Activity scenario descriptions

**Key Elements to Include:**
- **Hero Section:** Activity headline, user in action image, primary benefit, "Shop for [Activity]" CTA
- **Why Activity-Specific Matters:** Generic shoes fail because... (2-3 paragraphs)
- **Key Requirements for Activity:**
  - Biomechanical demands
  - Stress points and impact
  - Duration considerations
  - Terrain/surface factors
  - Weather/condition needs
- **Performance Features (5-8):**
  - Feature name
  - Why it matters for this activity
  - Technology/material used
  - Performance benefit
  - Testing/validation data
- **Recommended Products:** 2-4 shoes ideal for activity with differentiation
- **Activity-Specific Technology:**
  - Cushioning system for impact
  - Support structure for motion control
  - Outsole design for traction/surface
  - Upper materials for breathability/protection
  - Weight optimization for efficiency
- **Performance Specs:**
  - Weight
  - Drop/heel-toe offset
  - Cushioning level
  - Stability rating
  - Durability expectations (miles/hours)
- **Activity User Testimonials (6-10):**
  - User name + photo
  - Activity level (beginner/intermediate/advanced)
  - Distance/duration they use for
  - What they love about fit/performance
  - Previous shoe comparison
  - "Shop This Shoe" CTA
- **Usage Scenarios:** Detailed descriptions of activity situations
- **Expert Endorsements:** Trainer, coach, or activity professional validation
- **Terrain/Condition Guide:** Which shoe for which scenario
- **Break-In & Durability:** What to expect over activity lifespan
- **Care & Maintenance:** Keep performing for activity
- **Comparison Chart:** Activity shoes vs. general shoes (4-6 rows)
- **Activity Tips:** Performance advice beyond shoes
- **CTAs:** Hero, with products, after testimonials (5-6 total)
- **FAQ:** 5-7 questions (Right for my level? Works for distance? Terrain suitable? Replace when? Break-in?)

**Goal:** Convert activity-specific shoppers into confident buyers by demonstrating deep understanding of their activity's unique demands and proving your shoes are engineered specifically for those requirements with performance features, testing validation, and testimonials from real users doing the same activity successfully.`,
  },
  {
    icon: <Palette />,
    title: "Profession-Specific Landing Page",
    description:
      "Create a page addressing unique profession needs for [a profession], featuring testimonials from workers with specific use case details.",
    prompt: `**Build a profession-specific landing page that addresses unique occupational footwear needs with testimonials from workers in that field.**

**PROFESSION FOCUS:** e.g., "Shoes for Nurses" OR "Restaurant & Service Industry" OR "Warehouse Workers" OR "Teachers on Their Feet All Day"

**OCCUPATIONAL DEMANDS:** e.g., 12-hour shifts, slip resistance, standing on concrete, quick movements

**Target Customer:** Choose one:
- [ ] Career professionals (need work-specific solutions)
- [ ] Pain-sufferers (current shoes causing problems)
- [ ] Safety-conscious (need protection features)

**Tone & Style:**
- Understanding of job demands
- Practical and realistic
- "We know your work" empathy
- Performance under pressure focus

**CTA Guidelines:** Each CTA should:
- Reference profession ("Made for Nurses")
- Use workplace language ("Survive Your Shift")
- Include job benefit ("Work Pain-Free")
- Emphasize understanding ("We Know Your Job")

**Conversion Tactics Use:**
- Job-specific pain point validation
- Workplace hazard addressing
- Shift duration capability
- Profession testimonials with shift details
- Safety certifications if applicable
- Cost-per-wear justification

**Key Elements to Include:**
- **Hero Section:** Profession headline, worker in environment image, "Work Comfortably" CTA
- **We Understand Your Job:** Specific demands breakdown (2-3 paragraphs)
- **Unique Occupational Challenges:**
  - Hours on feet per shift
  - Surface type (concrete, tile, uneven)
  - Movement patterns (standing, walking, quick turns)
  - Environmental factors (spills, heat, cold)
  - Safety requirements
  - Dress code considerations
- **Essential Features for Profession (6-10):**
  - Feature name
  - Why critical for this job
  - Technology delivering it
  - Real-world benefit
  - Safety/compliance if relevant
- **Recommended Products:** 2-3 shoes ideal for profession with differentiation
- **Profession-Specific Technology:**
  - Slip-resistant outsoles (with rating)
  - Arch support for standing
  - Cushioning for concrete floors
  - Fluid/stain resistance
  - Breathability for long shifts
  - Antimicrobial linings
  - Easy-clean materials
- **Shift Survival Features:**
  - All-day comfort engineering
  - Fatigue reduction technology
  - Moisture management
  - Odor control
  - Quick recovery between shifts
- **Professional Testimonials (8-12):**
  - Worker name + photo
  - Job title/department
  - Shift length and frequency
  - Specific work environment details
  - Pain problems solved
  - Performance improvements
  - How long they've worn
  - "I recommend to coworkers"
  - Verification badge if possible
- **Workplace Scenarios:** Day-in-the-life descriptions
- **Safety Certifications:** ASTM, slip ratings, electrical hazard if relevant
- **Durability for Job Demands:** Expected lifespan in profession use
- **Cost Analysis:** Investment vs. cheaper shoes that fail fast
- **Care for Workplace:** Cleaning spills, maintaining in harsh conditions
- **Comparison Chart:** Work shoes vs. regular athletic (5-7 rows)
- **Employer Purchase:** Bulk/corporate ordering if applicable
- **Professional Organization Endorsements:** Union, association recommendations if available
- **CTAs:** Hero, with products, after testimonials (5-6 total)
- **FAQ:** 6-8 questions (Slip rating? Last how long? Safety certified? Dress code compliant? Replace when? Bulk orders?)

**Goal:** Convert profession-specific workers into loyal customers by demonstrating deep understanding of their unique occupational footwear demands and proving through worker testimonials that your shoes deliver the safety, comfort, and durability required to perform their job pain-free shift after shift in their specific work environment.`,
  },
  {
    icon: <Tags />,
    title: "Style Lookbook Landing Page",
    description:
      "Create a lookbook page showing shoes styled with outfits, with outfit component links for complete-the-look shopping and an Add to Cart CTA for each.",
    prompt: `**Build a style lookbook landing page with shoes styled in outfits featuring shoppable links for complete-the-look purchasing.**

**LOOKBOOK THEME:** e.g., "Spring Style Guide" OR "Casual Everyday Looks" OR "Work to Weekend" OR "Summer Outfit Inspiration"

**STYLE FOCUS:** e.g., Seasonal, occasion-based, trend-driven, lifestyle-specific

**Visitor Type:** Choose one:
- [ ] Visual shoppers (need to see styled)
- [ ] Outfit inspiration seekers (how to wear)
- [ ] Complete-look buyers (shopping multiple items)

**Tone & Style:**
- Fashion editorial energy
- Aspirational but wearable
- Minimal text, maximum visual
- "Style made simple" approachability

**CTA Guidelines:** Each CTA should:
- Be direct and simple ("Shop Shoes", "Get Look", "Add")
- Reference complete styling ("Shop Full Outfit")
- Use ease language ("Complete the Look")
- Include price visibility

**Conversion Tactics Use:**
- Outfit-focused shoe presentation
- Multiple styling examples per shoe
- Complete-look bundles with discount
- Outfit component links (clothing partners)
- Price transparency for full looks
- Occasion-based organization

**Key Elements to Include:**
- **Hero Section:** Lookbook title, hero styled image, "Browse Looks" CTA
- **Lookbook Introduction:** Seasonal/thematic statement (1-2 paragraphs)
- **Styled Outfit Photos (10-20):**
  - High-quality full-outfit photography
  - Shoes prominently featured
  - Lifestyle setting/context
  - Clean, editorial aesthetic
- **Shoppable Product Details per Look:**
  - Shoes with price + "Shop Shoes" CTA
  - Top/shirt with link/price
  - Bottoms with link/price
  - Accessories with link/price
  - "Shop Complete Look" bundle CTA
- **Hover/Click Functionality:** Product info appears on image interaction
- **Multiple Angles:** Same shoes styled 3-5 different ways
- **Occasion Categories:**
  - Casual everyday (3-5 looks)
  - Work/professional (3-5 looks)
  - Evening/going out (3-5 looks)
  - Weekend/active (3-5 looks)
- **Styling Notes:** Brief tips on creating each look
- **Mix & Match Guide:** How shoes work across multiple outfits
- **Price Per Look:** Total outfit cost displayed
- **Budget Alternatives:** "Get the look for less" options
- **Seasonal Appropriateness:** Weather/occasion context
- **Model Details:** Height, size worn for fit reference
- **Quick Add:** Add shoes without leaving lookbook
- **Shopping Cart Preview:** Running total as items added
- **Save/Share Looks:** Wishlist or share favorite outfits
- **Customer Recreation Gallery:** Real customers recreating looks (4-6 photos)
- **Outfit Formulas:** "Classic denim + white tee + our sneakers"
- **CTAs:** On each shoe, complete look bundles (numerous throughout)
- **FAQ:** 3-4 questions (Buy separately? Clothing partners? Sizing? Returns?)

**Goal:** Convert visual shoppers into buyers by seamlessly integrating shoes into aspirational outfit styling that shows exactly how to wear them, with frictionless complete-the-look shopping that increases basket size through outfit bundles while inspiring confidence that they're buying shoes that fit their lifestyle and style needs.`,
  },
  {
    icon: <CircleCheck />,
    title: "Seasonal Collection Pages",
    description:
      "Build a seasonal collection page organized by style with outfit styling and customer photos showing different body types.",
    prompt: `**Build a seasonal collection page organized by style with outfit inspiration and diverse customer photos showing shoes on different body types.**

**SEASONAL FOCUS:** e.g., "Spring Collection" OR "Fall Essentials" OR "Summer Styles" OR "Winter Collection"

**STYLE ORGANIZATION:** e.g., Casual, dressy, athletic, work, trendy

**Shopper Type:** Choose one:
- [ ] Seasonal refreshers (updating for new season)
- [ ] Trend followers (want current styles)
- [ ] Body-conscious (need to see on similar physique)

**Tone & Style:**
- Seasonal excitement and freshness
- Inclusive and body-positive
- Trend-aware but timeless
- "Something for everyone" welcoming

**CTA Guidelines:** Each CTA should:
- Emphasize seasonality ("Perfect for Spring")
- Use style category ("Shop Casual Styles")
- Reference versatility ("Style It Your Way")
- Include timeliness ("New This Season")

**Conversion Tactics Use:**
- Style category organization
- Seasonal color/material highlights
- Diverse body type representation
- Multiple styling per shoe
- Early season availability messaging
- Complete seasonal wardrobe bundles

**Key Elements to Include:**
- **Hero Section:** Seasonal headline, hero collection image, "Shop Collection" CTA
- **Seasonal Story:** Why these styles now, trend inspiration (2-3 paragraphs)
- **Collection by Style Category:**
  - Casual everyday (4-8 styles)
  - Dressy/occasion (4-8 styles)
  - Athletic/active (4-8 styles)
  - Work-appropriate (4-8 styles)
  - Trend-forward (4-8 styles)
- **Seasonal Features Highlighted:**
  - Materials (breathable mesh, waterproof, insulated)
  - Colors (spring pastels, fall earth tones)
  - Trends (chunky soles, minimal designs)
  - Weather-appropriate tech
- **Professional Styling Photos (8-15):**
  - Full-body outfit styling
  - Multiple looks per shoe
  - Seasonal-appropriate outfits
  - Various style categories
- **Customer Photo Gallery (15-25):**
  - Real customers wearing collection
  - Diverse body types (petite, average, tall, plus-size)
  - Height and size information
  - Different styling approaches
  - Body type tags for filtering
  - Casual to dressy occasions
- **Style Category Filters:** Browse by aesthetic preference
- **Body Type Filters:** See shoes on similar physiques
- **Outfit Ideas:** Seasonal styling suggestions per shoe
- **Color Palette:** Seasonal colors featured throughout
- **Versatility Showcase:** Same shoe styled multiple ways
- **New Arrivals:** "Just dropped" section
- **Seasonal Bundles:** Multi-shoe sets for complete wardrobe
- **Limited Availability:** "Seasonal styles - limited quantities"
- **Styling Tips:** How to transition between seasons
- **Mix & Match:** Cross-category outfit combinations
- **Customer Testimonials:** Seasonal satisfaction stories
- **Size Inclusivity:** Wide range represented in photos
- **CTAs:** Hero, style categories, bundles, customer gallery (6-8 total)
- **FAQ:** 4-5 questions (Restock? Seasonal only? Available year-round? Fit true to size? Weather-appropriate?)

**Goal:** Maximize seasonal revenue by creating timely urgency around new collection organized by clear style categories that help shoppers find their aesthetic, while building purchase confidence through diverse customer photos showing shoes on various body types that prove versatility and inclusive fit across different physiques and styling preferences.`,
  },
  {
    icon: <Expand />,
    title: "Comfort-Focused Landing Page",
    description:
      "Create a page with expert endorsements, comfort technology explanations, and profession-specific testimonials.",
    prompt: `**Build a comfort-focused landing page that positions your shoes as pain-relief solutions through expert validation, technology breakdowns, and profession testimonials.**

**COMFORT FOCUS:** e.g., "All-Day Comfort Technology" OR "Pain-Free Footwear" OR "Engineered for Comfort"

**COMFORT PRIORITIES:** e.g., Arch support, cushioning, pressure relief, fatigue reduction

**Target Customer:** Choose one:
- [ ] Pain-sufferers (feet/back/joint issues)
- [ ] Long-hours workers (need all-day comfort)
- [ ] Comfort-prioritizers (won't sacrifice for style)

**Tone & Style:**
- Empathetic comfort expert
- Science and engineering-focused
- Relief and solution-oriented
- "Finally, shoes that don't hurt"

**CTA Guidelines:** Each CTA should:
- Reference pain relief ("End Foot Pain")
- Use comfort language ("Walk Pain-Free")
- Include technology ("Engineered Comfort")
- Emphasize all-day capability ("Comfort That Lasts")

**Conversion Tactics Use:**
- Podiatrist endorsements
- Biomechanics explanations
- Comfort technology diagrams
- Profession-specific validation
- Pain point before/after stories
- Comfort guarantee

**Key Elements to Include:**
- **Hero Section:** "All-Day Comfort Guaranteed" headline, comfort image, "Experience Comfort" CTA
- **The Comfort Problem:** Why most shoes hurt (2-3 paragraphs on industry failures)
- **Expert Endorsements:**
  - Podiatrist quotes (3-5 doctors)
  - Credentials and affiliations
  - Clinical perspective on comfort needs
  - Recommendation statements
  - Professional headshots
- **Comfort Technology Breakdown (5-8 features):**
  - Technology name
  - What it does biomechanically
  - Materials and engineering
  - Visual diagram/cross-section
  - Pain point it addresses
  - Clinical/testing validation
- **Key Comfort Innovations:**
  - Advanced cushioning system
  - Anatomical arch support
  - Pressure distribution technology
  - Shock absorption
  - Heel cup design
  - Forefoot flex zones
  - Breathable comfort
- **Biomechanics Education:** How proper support prevents pain
- **Profession-Specific Testimonials (10-15):**
  - Nurse: "12-hour shifts, no pain"
  - Teacher: "Standing all day, still comfortable"
  - Retail worker: "Concrete floors, no problem"
  - Server: "Running around, feet feel great"
  - Warehouse: "Heavy lifting, back pain gone"
  - Office worker: "Walking meetings, no fatigue"
  - Each with photo, job details, pain solved, hours worn
- **Pain Point Solutions:**
  - Plantar fasciitis relief
  - Heel pain elimination
  - Arch support for flat feet
  - Bunion accommodation
  - Morton's neuroma relief
  - Knee/back pain reduction
- **Comfort Testing Data:** Lab results, wear testing, pressure mapping
- **Comparison Chart:** Our comfort tech vs. standard shoes (6-8 rows)
- **All-Day Comfort Timeline:** Hour-by-hour comfort maintenance
- **Medical Conditions:** Diabetes-friendly, arthritis-appropriate, orthotic-compatible
- **Comfort Guarantee:** "30-day comfort promise or money back"
- **Break-In Period:** "Comfortable from day one" messaging
- **Professional Recognition:** Awards, certifications (APMA Seal if applicable)
- **CTAs:** Hero, after technology, with testimonials, guarantee (5-6 total)
- **FAQ:** 6-8 questions (Podiatrist recommended? Works for condition? Orthotic-friendly? Replace when? Really no break-in? Guarantee details?)

**Goal:** Convert pain-suffering and comfort-prioritizing shoppers into confident buyers by providing overwhelming expert validation, detailed comfort technology explanations, and profession-specific testimonials that prove your shoes deliver lasting, pain-free comfort backed by podiatric science and money-back guarantee.`,
  },
  {
    icon: <Timer />,
    title: "Width-Specific Landing Page",
    description:
      "Build pages addressing specific fit concerns, showing product differences, and featuring customer reviews with measurements.",
    prompt: `**Build a width-specific landing page that addresses unique fit challenges and showcases products designed for specific width needs with detailed customer reviews.**

**WIDTH FOCUS:** e.g., "Wide Width Shoes" OR "Narrow Fit Collection" OR "Extra Wide Options" OR "Slim Fit Shoes"

**FIT CHALLENGES:** e.g., Pinching, gapping, blisters, instability, sizing struggles

**Target Customer:** Choose one:
- [ ] Width-frustrated (standard widths never fit)
- [ ] Online hesitant (scared to buy without trying)
- [ ] Measurement-conscious (know their exact needs)

**Tone & Style:**
- Understanding fit specialist
- Validating of width struggles
- Technical precision
- "Finally, shoes made for you" relief

**CTA Guidelines:** Each CTA should:
- Reference width specifically ("Shop Wide Width")
- Use fit confidence language ("Built for Your Width")
- Include comfort benefit ("No More Pinching")
- Emphasize design ("Engineered Wide")

**Conversion Tactics Use:**
- Width measurement guide
- Design differences from standard
- Customer reviews with foot measurements
- Fit comparison visuals
- Width-specific guarantees
- Multiple width options per style

**Key Elements to Include:**
- **Hero Section:** Width-specific headline, "Shop [Width] Collection" CTA
- **You're Not Alone:** Statistics on width needs, validation (2-3 paragraphs)
- **Common Width Problems:**
  - Narrow: Heel slipping, instability, blisters from movement
  - Wide: Pinching sides, toe squeeze, bunion pressure, top-of-foot pain
  - Extra Wide: No options available, sizing up doesn't work
- **How to Measure Your Width:**
  - Step-by-step guide with images
  - Width measurement chart
  - Letter sizing explained (B/D/2E/4E/6E)
  - When you need this width
- **Design Differences Explained:**
  - Last shape modifications
  - Toe box dimensions
  - Midfoot accommodation
  - Heel cup adjustments
  - Material flexibility
  - Construction differences
- **Width-Specific Collection:** Products available in this width (8-20 styles)
- **Comparison Visuals:** Standard width vs. your width (top view, side view)
- **Technical Specifications:**
  - Exact width measurements at key points
  - Volume accommodation
  - Adjustability features
  - Material stretch properties
- **Customer Reviews with Measurements (12-20):**
  - Customer name + photo
  - Exact foot measurements (length + width)
  - Previous fit struggles
  - Size/width ordered
  - Fit experience detailed
  - Comfort after hours worn
  - Problem areas solved
  - Would recommend to similar feet
- **Width Success Stories:** Detailed testimonials about finding proper width
- **Fit Comparison Chart:** Features across width options
- **Style Variety Showcase:** "All styles, your width" message
- **Sizing Confidence Tools:**
  - Width quiz/calculator
  - Video fit guide
  - Chat with fit experts
- **Fit Guarantee:** "Perfect width fit or free exchange"
- **Width Availability:** Which styles in which widths
- **Special Considerations:** Orthotics, swelling, specific conditions
- **Expert Validation:** Podiatrist perspective on proper width
- **CTAs:** Hero, with collection, after reviews, guarantee (5-6 total)
- **FAQ:** 7-9 questions (How to measure? Between widths? Narrow vs. regular? Width changes over time? All styles available? Returns? Custom widths?)

**Goal:** Convert width-frustrated shoppers into loyal customers by validating their unique fit struggles, providing precise measurement guidance, and proving through detailed customer reviews with measurements that your width-specific designs deliver proper fit that standard shoes can't - backed by fit guarantee and technical specifications that build confidence.`,
  },
  {
    icon: <Sparkles />,
    title: "Risk Reduction Landing Page",
    description:
      "Create a page with prominent return/exchange policy, step by step guides showing a simple return process, and testimonials from exchangers.",
    prompt: `**Build a risk reduction landing page that eliminates purchase anxiety through transparent return policy, simple process demonstration, and exchanger testimonials.**

**RISK REDUCTION FOCUS:** e.g., "Perfect Fit Guaranteed" OR "Risk-Free Shoe Shopping" OR "Free Returns & Exchanges Always"

**POLICY HIGHLIGHTS:** e.g., Free returns, extended window, unlimited exchanges, no restocking fees

**Target Customer:** Choose one:
- [ ] Online-hesitant (scared to buy shoes without trying)
- [ ] Return-burned (bad experiences with other brands)
- [ ] Fit-anxious (between sizes, uncertain)

**Tone & Style:**
- Reassuring and confident
- Simple and transparent
- Customer-first approach
- "Shop without worry" empowerment

**CTA Guidelines:** Each CTA should:
- Emphasize risk-free ("Shop Risk-Free")
- Use guarantee language ("100% Satisfaction")
- Include ease messaging ("Easy Returns")
- Reference confidence ("Buy with Confidence")

**Conversion Tactics Use:**
- Extended return window emphasis
- Free return shipping highlight
- Multiple exchange allowances
- Instant refund timeline
- Exchanger success stories
- No-hassle promise

**Key Elements to Include:**
- **Hero Section:** "Risk-Free Shopping - Perfect Fit Guaranteed" headline, "Shop with Confidence" CTA
- **Return Policy Highlights:**
  - Return window (60, 90, 365 days)
  - Free return shipping both ways
  - No restocking fees
  - Unlimited exchanges
  - Instant refunds
  - Original condition not required (reasonable wear)
- **Why We Offer This:** Customer-first philosophy (2-3 paragraphs)
- **How Returns Work - Step-by-Step (with visuals):**
  - Step 1: Request return online or via app (screenshot of portal)
  - Step 2: Print prepaid label or schedule pickup
  - Step 3: Pack shoes (any box works)
  - Step 4: Drop off or hand to courier
  - Step 5: Refund processed when received (2-3 days)
- **How Exchanges Work:**
  - Same simple process
  - Advanced exchange option (we ship first, you return after)
  - Try multiple sizes at once
  - Unlimited attempts to find perfect fit
- **Video Walkthrough:** 90-second demo of return portal and process
- **Timeline Transparency:**
  - Return initiated: Instant
  - Label generated: Within 1 hour
  - Package shipped back: Your timeline
  - Received at warehouse: 3-7 days
  - Refund processed: Within 24 hours
  - Money back in account: 2-3 business days
- **Exchanger Testimonials (8-12):**
  - Customer name + photo
  - What they originally ordered
  - Why they exchanged (size, width, style)
  - How many exchanges before perfect fit
  - Experience with process
  - Final satisfaction with shoes
  - "Process was easy"
- **Common Scenarios Covered:**
  - Between sizes
  - Wrong width
  - Changed mind on style
  - Fit issues discovered after wearing
  - Seasonal weather changes
  - Medical/foot changes
- **Success Metrics:** "95% find perfect fit within 2 exchanges"
- **No-Hassle Promise:** No questions asked, no forms, no hurdles
- **Customer Service Support:** Live chat, phone support during process
- **Return Condition Policy:** Reasonable wear acceptable, detailed examples
- **International Returns:** If applicable, simplified process
- **Gift Returns:** Easy exchange for recipients
- **Comparison Chart:** Our policy vs. typical retailers (6-8 rows)
- **Cost Breakdown:** "$0 to try, $0 to return, $0 to exchange"
- **FAQ:** 8-10 questions (How long? Condition requirements? International? Gift returns? Exchanges unlimited? Refund timing? Original packaging needed? Worn outside?)
- **CTAs:** Hero, after process, after testimonials, with policy details (5-6 total)

**Goal:** Convert online-hesitant shoppers into confident buyers by removing all purchase risk through radically simple, transparent return and exchange policies demonstrated with step-by-step visuals and validated by exchanger testimonials that prove finding the perfect fit is truly worry-free - making "just try it" the obvious choice.`,
  },
];
